/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {dlsColor} from '../../../theme/theme';

export interface DataTableColumn {
  key: string;
  label: string;
  // a fixed column width - columns without one share the remaining width ...
  width?: number | string;
}

export type DataTableRow = Record<string, string>;

export interface DataTableProps {
  columns: DataTableColumn[];
  rows: DataTableRow[];
  // a stable key per row - the row index by default ...
  getRowKey?: (row: DataTableRow, index: number) => string;
  className?: string;
}

const useStyles = makeStyles(() => ({
  // the bordered, rounded frame - it grows with its rows and, once its
  // parent caps its height, scrolls its body under the sticky header ...
  frame : {
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    width           : '100%',
    minHeight       : 0,
    overflow        : 'hidden',
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey3')}`,
    borderRadius    : '12px'
  },
  scroller : {
    flex                   : '1 1 auto',
    minHeight              : 0,
    overflowX              : 'hidden',
    overflowY              : 'auto',
    '&::-webkit-scrollbar' : {
      width : '10px'
    },
    '&::-webkit-scrollbar-thumb' : {
      backgroundColor : dlsColor('grey5'),
      borderRadius    : '100px',
      border          : `2px solid ${dlsColor('white')}`
    }
  },
  table : {
    width          : '100%',
    tableLayout    : 'fixed',
    borderSpacing  : 0,
    borderCollapse : 'separate'
  },
  headerCell : {
    position        : 'sticky',
    top             : 0,
    zIndex          : 1,
    boxSizing       : 'border-box',
    height          : '37px',
    padding         : '10px 16px 10px 12px',
    textAlign       : 'left',
    verticalAlign   : 'middle',
    backgroundColor : dlsColor('grey7'),
    borderLeft      : `1px solid ${dlsColor('grey13')}`,
    fontFamily      : 'Inter',
    fontWeight      : 600,
    fontSize        : '14px',
    lineHeight      : '16px',
    color           : dlsColor('grey2'),
    '&:first-child' : {
      borderLeft : 'none'
    }
  },
  row : {
    backgroundColor     : dlsColor('white'),
    '&:nth-child(even)' : {
      backgroundColor : dlsColor('grey13')
    }
  },
  // the cell's text wraps, so a long value (a fault's description) grows
  // its row instead of overflowing it ...
  cell : {
    boxSizing       : 'border-box',
    height          : '53px',
    padding         : '10px 16px 10px 12px',
    verticalAlign   : 'middle',
    borderTop       : `1px solid ${dlsColor('grey5')}`,
    borderLeft      : `1px solid ${dlsColor('grey5')}`,
    overflowWrap    : 'anywhere',
    fontFamily      : 'Inter',
    fontWeight      : 500,
    fontSize        : '12px',
    lineHeight      : '16px',
    color           : dlsColor('grey2'),
    '&:first-child' : {
      borderLeft : 'none'
    }
  }
}));

const getRowIndexKey = (row: DataTableRow, index: number) => String(index);

/**
 * Renders the current theme's data table - a bordered, rounded table with a
 * sticky grey header row, striped body rows and column dividers, whose cells
 * wrap so long values grow their row. The table grows with its rows; once
 * its parent caps its height, only its body scrolls (vertically) under the
 * sticky header. A plain, presentational table - no sorting, paging or
 * virtualization (use IonGrid for those).
 *
 * ```typescript
 *
 *   import {DataTable} from '@ion/dls/src/components/table/current/DataTable';
 *   ...
 *
 *   <DataTable columns={[
 *       {key: 'dtcCode', label: 'DTC Code', width: 114},
 *       {key: 'description', label: 'Description'}
 *     ]}
 *     rows={[{dtcCode: 'P4954', description: 'Throttle position sensor'}]} />
 *
 * ```
 *
 * @param   columns   The table's columns, in order.
 * @param   rows      The table's rows, keyed by column key.
 * @param   getRowKey A stable key per row - the row index by default.
 * @param   className A class name for the table's frame.
 * @returns           The data table.
 */
export const DataTable = ({
  columns,
  rows,
  getRowKey = getRowIndexKey,
  className
}: DataTableProps) => {
  const classes = useStyles();

  return (
    <div className={classnames(classes.frame, className)}>
      <div className={classes.scroller}>
        <table className={classes.table}>
          <colgroup>
            {columns.map(function(column) {
              return (
                <col key={column.key} style={{width: column.width}} />
              );
            })}
          </colgroup>
          <thead>
            <tr>
              {columns.map(function(column) {
                return (
                  <th key={column.key} scope='col' className={classes.headerCell}>
                    {column.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map(function(row, index) {
              return (
                <tr key={getRowKey(row, index)} className={classes.row}>
                  {columns.map(function(column) {
                    return (
                      <td key={column.key} className={classes.cell}>
                        {row[column.key]}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default memo(DataTable);
